import React, { useEffect, useMemo, useRef, useState } from 'react';

const OPENROUTER_CHART_URL = 'https://openrouter.ai/api/frontend/v1/rankings/model-rankings-chart';
const OPENROUTER_LEADERBOARD_URL = 'https://openrouter.ai/api/frontend/v1/rankings/models';

const MODEL_COLORS = [
    '#14a77f', '#3287db', '#7a55d7', '#ee7446', '#009da2',
    '#8ebf28', '#d7a029', '#ae5d45', '#7051b8', '#42b177',
];

const KNOWN_MODEL_NAMES = {
    'deepseek/deepseek-v4-flash-20260731': 'DeepSeek V4 Flash 0731',
    'deepseek/deepseek-v4-flash-20260423': 'DeepSeek V4 Flash 0423',
    'deepseek/deepseek-v4-pro-20260423': 'DeepSeek V4 Pro',
    'tencent/hy3-20260706': 'Hy3',
    'xiaomi/mimo-v2.5-20260422': 'MiMo-V2.5',
    'openai/gpt-5.6-luna-20260709': 'GPT-5.6 Luna',
    'z-ai/glm-5.2-20260616': 'GLM 5.2',
    'nvidia/nemotron-3-ultra-550b-a55b-20260604': 'Nemotron 3 Ultra',
    'google/gemini-3.6-flash-20260721': 'Gemini 3.6 Flash',
    'poolside/laguna-s-2.1-20260720': 'Laguna S 2.1',
};

const compactDate = (date, isZh) => {
    const parsed = new Date(`${date}T00:00:00Z`);
    if (Number.isNaN(parsed.getTime())) return date;
    return new Intl.DateTimeFormat(isZh ? 'zh-CN' : 'en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    }).format(parsed);
};

const hashString = (value) => [...value].reduce((hash, character) => (
    ((hash << 5) - hash) + character.charCodeAt(0)
), 0);

export const colorForModel = (model) => {
    if (model === 'Others') return '#ef4f9d';
    return MODEL_COLORS[Math.abs(hashString(model)) % MODEL_COLORS.length];
};

export const totalForWeek = (point) => Object.values(point?.ys || {}).reduce((sum, value) => sum + Number(value || 0), 0);

export const formatTokenCount = (value, digits = 1) => {
    const numericValue = Number(value || 0);
    if (numericValue >= 1e12) return `${(numericValue / 1e12).toFixed(digits)}T`;
    if (numericValue >= 1e9) return `${(numericValue / 1e9).toFixed(digits)}B`;
    if (numericValue >= 1e6) return `${(numericValue / 1e6).toFixed(digits)}M`;
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(numericValue);
};

export const formatModelName = (permaslug) => {
    const [standardSlug, variant] = permaslug.split(':');
    const known = KNOWN_MODEL_NAMES[standardSlug];
    if (known) return variant === 'free' ? `${known} (free)` : known;

    const rawName = standardSlug.split('/').pop()
        .replace(/-\d{8}$/, '')
        .replace(/-/g, ' ')
        .replace(/\bgpt\b/gi, 'GPT')
        .replace(/\bglm\b/gi, 'GLM')
        .replace(/\bai\b/gi, 'AI')
        .replace(/\b\w/g, (character) => character.toUpperCase());
    return variant === 'free' ? `${rawName} (free)` : rawName;
};

export const normalizeLeaderboard = (payload) => {
    const rows = Array.isArray(payload?.data) ? payload.data : [];
    const grouped = new Map();

    rows.forEach((row) => {
        const key = row.variant_permaslug || row.model_permaslug;
        if (!key) return;
        const current = grouped.get(key) || {
            permaslug: key,
            tokens: 0,
            change: row.change,
        };
        current.tokens += Number(row.total_prompt_tokens || 0) + Number(row.total_completion_tokens || 0);
        if (current.change == null && row.change != null) current.change = row.change;
        grouped.set(key, current);
    });

    return [...grouped.values()].sort((left, right) => right.tokens - left.tokens);
};

const normalizeChart = (payload) => {
    const rows = payload?.data?.data;
    return Array.isArray(rows) ? rows.filter((row) => row?.x && row?.ys) : [];
};

export const useOpenRouterRankings = (dataset) => {
    const [chartData, setChartData] = useState(dataset.fallbackChart || []);
    const [chartCachedAt, setChartCachedAt] = useState(new Date(`${dataset.asOf}T23:59:59Z`).getTime());
    const [leaderboard, setLeaderboard] = useState(dataset.fallbackLeaderboard || []);
    const [leaderboardView, setLeaderboardView] = useState('week');
    const [chartStatus, setChartStatus] = useState('snapshot');
    const [leaderboardStatus, setLeaderboardStatus] = useState('snapshot');

    useEffect(() => {
        if (typeof fetch !== 'function') return undefined;
        const controller = new AbortController();
        fetch(OPENROUTER_CHART_URL, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) throw new Error(`OpenRouter chart request failed: ${response.status}`);
                return response.json();
            })
            .then((payload) => {
                const normalized = normalizeChart(payload);
                if (normalized.length) {
                    setChartData(normalized);
                    if (payload?.data?.cachedAt) setChartCachedAt(payload.data.cachedAt);
                    setChartStatus('live');
                }
            })
            .catch((error) => {
                if (error.name !== 'AbortError') setChartStatus('snapshot');
            });
        return () => controller.abort();
    }, []);

    useEffect(() => {
        if (typeof fetch !== 'function') return undefined;
        const controller = new AbortController();
        setLeaderboardStatus('loading');
        fetch(`${OPENROUTER_LEADERBOARD_URL}?view=${leaderboardView}`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) throw new Error(`OpenRouter leaderboard request failed: ${response.status}`);
                return response.json();
            })
            .then((payload) => {
                const normalized = normalizeLeaderboard(payload);
                if (normalized.length) {
                    setLeaderboard(normalized);
                    setLeaderboardStatus('live');
                }
            })
            .catch((error) => {
                if (error.name !== 'AbortError') setLeaderboardStatus('snapshot');
            });
        return () => controller.abort();
    }, [leaderboardView]);

    return {
        chartData,
        chartCachedAt,
        chartStatus,
        leaderboard,
        leaderboardStatus,
        leaderboardView,
        setLeaderboardView,
    };
};

const weekSegments = (point) => Object.entries(point?.ys || {})
    .map(([model, value]) => ({ model, value: Number(value || 0) }))
    .filter((segment) => segment.value > 0)
    .sort((left, right) => (left.model === 'Others' ? -1 : right.model === 'Others' ? 1 : right.value - left.value));

export const OpenRouterTokenChart = ({ data, cachedAt, status, isZh = false }) => {
    const [scale, setScale] = useState('linear');
    const [activeIndex, setActiveIndex] = useState(null);
    const scrollRef = useRef(null);
    const width = 1120;
    const height = 386;
    const margin = { top: 24, right: 16, bottom: 44, left: 58 };
    const plotWidth = width - margin.left - margin.right;
    const plotHeight = height - margin.top - margin.bottom;

    useEffect(() => {
        const scrollToLatest = () => {
            const element = scrollRef.current;
            if (element && window.innerWidth <= 760) element.scrollLeft = element.scrollWidth;
        };
        scrollToLatest();
        window.addEventListener('resize', scrollToLatest);
        return () => window.removeEventListener('resize', scrollToLatest);
    }, [data.length]);

    const { totals, modelKeys } = useMemo(() => {
        const aggregate = new Map();
        data.forEach((point) => Object.entries(point.ys || {}).forEach(([model, value]) => {
            if (model !== 'Others') aggregate.set(model, (aggregate.get(model) || 0) + Number(value || 0));
        }));
        return {
            totals: data.map(totalForWeek),
            modelKeys: [...aggregate.entries()]
                .sort((left, right) => right[1] - left[1])
                .slice(0, 8)
                .map(([model]) => model),
        };
    }, [data]);

    if (!data.length) return <div className="ai-dev-chart-empty">OpenRouter data unavailable</div>;

    const lastWeekStart = new Date(`${data[data.length - 1].x}T00:00:00Z`).getTime();
    const elapsedDays = Math.min(7, Math.max(1, (Number(cachedAt || Date.now()) - lastWeekStart) / 86400000));
    const forecastTotal = elapsedDays < 6.9 ? totals[totals.length - 1] * (7 / elapsedDays) : totals[totals.length - 1];
    const rawMax = Math.max(...totals, forecastTotal, 1);
    const linearStep = Math.max(1e12, Math.ceil(rawMax / 4 / 1e13) * 1e13);
    const linearMax = linearStep * 4;
    const logFloor = 1e12;
    const logMax = Math.max(1e13, 10 ** Math.ceil(Math.log10(rawMax)));
    const maxValue = scale === 'linear' ? linearMax : logMax;
    const barSlot = plotWidth / data.length;
    const barWidth = Math.max(5, Math.min(18, barSlot - 2));
    const activePoint = activeIndex == null ? null : data[Math.min(activeIndex, data.length - 1)];
    const activeSegments = activePoint ? weekSegments(activePoint).sort((left, right) => right.value - left.value) : [];

    const valueToHeight = (value) => {
        if (scale === 'linear') return (value / maxValue) * plotHeight;
        const numerator = Math.log10(Math.max(value, logFloor)) - Math.log10(logFloor);
        const denominator = Math.log10(maxValue) - Math.log10(logFloor);
        return Math.max(0, numerator / denominator) * plotHeight;
    };

    const ticks = scale === 'linear'
        ? [0, linearStep, linearStep * 2, linearStep * 3, linearStep * 4]
        : [1e12, 1e13, 1e14].filter((value) => value <= logMax);
    const xTickIndexes = [...new Set([0, Math.round((data.length - 1) * 0.2), Math.round((data.length - 1) * 0.4), Math.round((data.length - 1) * 0.6), Math.round((data.length - 1) * 0.8), data.length - 1])];

    return (
        <div className="ai-dev-openrouter-chart">
            <div className="ai-dev-openrouter-chart__toolbar">
                <span>{status === 'live' ? (isZh ? '官方实时数据' : 'Official live data') : (isZh ? '本地快照兜底' : 'Local snapshot fallback')}</span>
                <div className="ai-dev-openrouter-segmented" aria-label={isZh ? '纵轴刻度' : 'Y-axis scale'}>
                    {['linear', 'log'].map((option) => (
                        <button
                            className={scale === option ? 'is-active' : ''}
                            key={option}
                            type="button"
                            onClick={() => setScale(option)}
                        >
                            {option === 'linear' ? 'Linear' : 'Log'}
                        </button>
                    ))}
                </div>
            </div>
            <div className="ai-dev-openrouter-chart__scroll" ref={scrollRef}>
                <div className="ai-dev-openrouter-chart__plot">
                    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={isZh ? 'OpenRouter 每周模型 Token 用量堆叠柱状图' : 'OpenRouter weekly token usage stacked by model'}>
                        <defs>
                            <pattern id="openrouterForecast" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                                <rect width="3" height="7" fill="rgba(255,255,255,0.55)" />
                            </pattern>
                        </defs>
                        {ticks.map((tick) => {
                            const y = margin.top + plotHeight - valueToHeight(tick);
                            return (
                                <g key={tick}>
                                    <line className="ai-dev-openrouter-chart__grid" x1={margin.left} x2={width - margin.right} y1={y} y2={y} />
                                    <text className="ai-dev-openrouter-chart__tick" x={margin.left - 12} y={y + 4} textAnchor="end">{tick === 0 ? '0' : formatTokenCount(tick, 0)}</text>
                                </g>
                            );
                        })}
                        {data.map((point, index) => {
                            const total = totals[index];
                            const targetHeight = valueToHeight(total);
                            const projectedHeight = index === data.length - 1 ? valueToHeight(forecastTotal) : targetHeight;
                            const segments = weekSegments(point);
                            const x = margin.left + (barSlot * index) + ((barSlot - barWidth) / 2);
                            let stackedHeight = 0;
                            return (
                                <g
                                    className="ai-dev-openrouter-chart__bar"
                                    key={point.x}
                                    role="button"
                                    tabIndex="0"
                                    aria-label={`${point.x}: ${formatTokenCount(total)} tokens`}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    onMouseLeave={() => setActiveIndex(null)}
                                    onFocus={() => setActiveIndex(index)}
                                    onBlur={() => setActiveIndex(null)}
                                >
                                    {segments.map((segment) => {
                                        const segmentHeight = total ? targetHeight * (segment.value / total) : 0;
                                        stackedHeight += segmentHeight;
                                        return (
                                            <rect
                                                key={segment.model}
                                                x={x}
                                                y={margin.top + plotHeight - stackedHeight}
                                                width={barWidth}
                                                height={Math.max(0, segmentHeight)}
                                                fill={colorForModel(segment.model)}
                                            />
                                        );
                                    })}
                                    {index === data.length - 1 && projectedHeight > targetHeight && (
                                        <>
                                            <rect x={x} y={margin.top + plotHeight - projectedHeight} width={barWidth} height={projectedHeight - targetHeight} fill="#d8e0e8" />
                                            <rect x={x} y={margin.top + plotHeight - projectedHeight} width={barWidth} height={projectedHeight - targetHeight} fill="url(#openrouterForecast)" />
                                        </>
                                    )}
                                    <rect className="ai-dev-openrouter-chart__hit" x={x - 2} y={margin.top} width={barWidth + 4} height={plotHeight} />
                                </g>
                            );
                        })}
                        {xTickIndexes.map((index) => {
                            const x = margin.left + (barSlot * index) + (barSlot / 2);
                            return <text className="ai-dev-openrouter-chart__tick" key={data[index].x} x={x} y={height - 15} textAnchor="middle">{compactDate(data[index].x, isZh)}</text>;
                        })}
                    </svg>
                    {activePoint && (
                        <div
                            className="ai-dev-openrouter-chart__tooltip"
                            style={{ left: `${Math.min(88, Math.max(12, (activeIndex / Math.max(1, data.length - 1)) * 100))}%` }}
                        >
                            <time>{activePoint.x}</time>
                            <strong>{formatTokenCount(totalForWeek(activePoint))} tokens</strong>
                            {activeSegments.slice(0, 4).map((segment) => (
                                <span key={segment.model}>
                                    <i style={{ background: colorForModel(segment.model) }} />
                                    <em>{segment.model === 'Others' ? (isZh ? '其他模型' : 'Other models') : formatModelName(segment.model)}</em>
                                    <b>{formatTokenCount(segment.value)}</b>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div className="ai-dev-openrouter-legend" aria-label={isZh ? '模型图例' : 'Model legend'}>
                {modelKeys.map((model) => (
                    <span key={model}><i style={{ background: colorForModel(model) }} />{formatModelName(model)}</span>
                ))}
                <span><i style={{ background: colorForModel('Others') }} />{isZh ? '其他模型' : 'Other models'}</span>
            </div>
            <p className="ai-dev-openrouter-chart__note">{isZh ? '斜纹表示仍在更新的当前周；总量为 Prompt 与 Completion Token 之和。' : 'Hatching marks the in-progress week; totals combine prompt and completion tokens.'}</p>
        </div>
    );
};

const changeLabel = (change, isZh) => {
    if (change == null) return isZh ? '新上榜' : 'new';
    const percent = change * 100;
    if (percent > 999) return '↑>999%';
    const arrow = percent >= 0 ? '↑' : '↓';
    return `${arrow}${Math.abs(percent).toFixed(0)}%`;
};

const LeaderboardColumn = ({ rows, offset, isZh }) => (
    <ol className="ai-dev-openrouter-leaderboard__column" start={offset + 1}>
        {rows.map((row, index) => {
            const author = row.permaslug.split('/')[0];
            const positive = row.change == null || row.change >= 0;
            return (
                <li key={row.permaslug}>
                    <span className="ai-dev-openrouter-leaderboard__rank">{index + offset + 1}.</span>
                    <i className="ai-dev-openrouter-leaderboard__dot" style={{ background: colorForModel(row.permaslug) }} />
                    <div className="ai-dev-openrouter-leaderboard__identity">
                        <strong title={formatModelName(row.permaslug)}>{formatModelName(row.permaslug)}</strong>
                        <span>{isZh ? '提供方' : 'by'} {author}</span>
                    </div>
                    <div className="ai-dev-openrouter-leaderboard__metric">
                        <strong>{formatTokenCount(row.tokens)} tokens</strong>
                        <span className={positive ? 'is-up' : 'is-down'}>{changeLabel(row.change, isZh)}</span>
                    </div>
                </li>
            );
        })}
    </ol>
);

export const OpenRouterLeaderboard = ({ rows, status, view, onViewChange, isZh = false }) => {
    const [modelFilter, setModelFilter] = useState('all');
    const filteredRows = rows.filter((row) => {
        const isFree = row.permaslug.endsWith(':free');
        if (modelFilter === 'free') return isFree;
        if (modelFilter === 'paid') return !isFree;
        return true;
    }).slice(0, 10);
    const midpoint = Math.ceil(filteredRows.length / 2);

    return (
        <div className="ai-dev-openrouter-leaderboard">
            <div className="ai-dev-openrouter-leaderboard__toolbar">
                <span>{status === 'loading' ? (isZh ? '更新中…' : 'Updating…') : status === 'live' ? (isZh ? '官方实时榜单' : 'Official live ranking') : (isZh ? '本地快照兜底' : 'Local snapshot fallback')}</span>
                <div>
                    <select aria-label={isZh ? '筛选模型类型' : 'Filter model type'} value={modelFilter} onChange={(event) => setModelFilter(event.target.value)}>
                        <option value="all">{isZh ? '全部模型' : 'All models'}</option>
                        <option value="free">{isZh ? '免费模型' : 'Free models'}</option>
                        <option value="paid">{isZh ? '付费模型' : 'Paid models'}</option>
                    </select>
                    <select aria-label={isZh ? '筛选榜单周期' : 'Filter ranking window'} value={view} onChange={(event) => onViewChange(event.target.value)}>
                        <option value="day">{isZh ? '今天' : 'Today'}</option>
                        <option value="week">{isZh ? '本周' : 'This Week'}</option>
                        <option value="month">{isZh ? '本月' : 'This Month'}</option>
                        <option value="trending">{isZh ? '趋势榜' : 'Trending'}</option>
                    </select>
                </div>
            </div>
            <div className="ai-dev-openrouter-leaderboard__grid">
                <LeaderboardColumn rows={filteredRows.slice(0, midpoint)} offset={0} isZh={isZh} />
                <LeaderboardColumn rows={filteredRows.slice(midpoint)} offset={midpoint} isZh={isZh} />
            </div>
        </div>
    );
};
